import { SegmentationIds } from 'constants/segmentation';
import { SegmentationStore } from 'contexts/segmentation';
import { FilterConfig } from 'interfaces/collection-filter';
import { CollectionProduct } from 'models/collection/collection';
import React from 'react';
import { checkIfProductCorresponds } from 'utils/collection/check-if-product-corresponds';
import { slugify } from 'utils/strings';

type CollecitonType = {
  products: CollectionProduct[];
  title: string;
};

export const useVisibleProductList = ({
  collection,
  activeFilter,
  activeAllergiesFilter,
  filterConfig,
  segmentation,
  shouldOmitPersonalization,
}: {
  collection?: CollecitonType | null;
  activeFilter: string[];
  activeAllergiesFilter?: string[];
  filterConfig?: FilterConfig;
  segmentation?: SegmentationIds | SegmentationStore;
  shouldOmitPersonalization?: boolean;
}): CollectionProduct[] => {
  // we need to slugify the tags
  const tagsMap = React.useMemo(() => {
    const map = {};
    if (!collection) {
      return map;
    }
    collection.products.forEach((product) => {
      map[product.id] = product.tags.map((tag) => slugify(tag));
    });
    return map;
  }, [collection]);

  const visibleProducts = React.useMemo(() => {
    if (!collection || !collection.products) {
      return [];
    }

    if (!filterConfig) {
      return [...collection.products];
    }

    // creates an object with each filter group as key and the selected filters in this group as array
    const assignedFilter = {} as { [key: string]: string[] };
    Object.entries(filterConfig).forEach(([filterKey, filterValue]) => {
      assignedFilter[filterKey] = filterValue.tags.filter((tag) =>
        activeFilter.includes(tag)
      );
    });

    // If the personalisation is enabled, we should sort the products by best seller,
    // in order to rank them on the top of the list.
    const sortedBestSellerProducts = shouldOmitPersonalization
      ? collection.products
      : collection.products.sort((a, b) => {
          if (!a.availableForSale || !b.availableForSale) {
            return 0;
          }

          const isBestSellerA = a.getIsBestSeller({ segment: segmentation });
          const isBestSellerB = b.getIsBestSeller({ segment: segmentation });

          if (isBestSellerA && !isBestSellerB) return -1;
          if (!isBestSellerA && isBestSellerB) return 1;
          return 0;
        });

    const products = sortedBestSellerProducts.filter((product) => {
      // Check if the product contains any tag from activeAllergiesFilter
      const hasAllergiesFilterTag = activeAllergiesFilter?.some((tag) =>
        tagsMap[product.id].includes(slugify(tag))
      );

      if (hasAllergiesFilterTag) return false;

      // loop over filter groups and check the filter
      const matchFilter = Object.keys(filterConfig).map((filterGroup) =>
        checkIfProductCorresponds(
          tagsMap[product.id],
          filterGroup,
          assignedFilter,
          filterConfig
        )
      );

      return matchFilter.every((checkedFilter) => checkedFilter === true);
    });
    return products;
  }, [
    activeAllergiesFilter,
    activeFilter,
    collection,
    filterConfig,
    segmentation,
    shouldOmitPersonalization,
    tagsMap,
  ]);

  return visibleProducts;
};
